/**
 * Shared MM/DD/YYYY date-of-birth field behaviour for WooCommerce checkout.
 *
 * Attaches the live mask (auto-slashes, month clamped to 01-12, day to 01-31)
 * and inline validation (valid calendar date, not in the future) used by both
 * the GHP checkout (`pages/ghp-checkout.js`) and the EA+ checkout
 * (`pages/checkout.js`). Integrates with WooCommerce `.form-row` validation
 * classes so it matches the rest of the checkout form.
 */

import {
  DIGIT_LIMIT,
  FULL_LENGTH,
  caretPositionAfterDigits,
  countDigitsBefore,
  expandAutofilledDate,
  formatDateMask,
  isBulkInsert,
  isFutureDate,
  isValidDob,
} from './dateMask.js';

const DEFAULT_ERROR_CLASS = 'ghp-field-error';
const DEFAULT_INVALID_MESSAGE = 'Please enter a valid date (MM/DD/YYYY).';
const DEFAULT_FUTURE_MESSAGE = 'Date of birth cannot be in the future.';

/**
 * Wire the mask + validation onto a date-of-birth input.
 *
 * Idempotent: a second call on the same element is a no-op, so it is safe to
 * call again after WooCommerce re-renders the checkout.
 *
 * @param {HTMLInputElement|null} el Input to enhance.
 * @param {{errorClass?: string, invalidMessage?: string, futureMessage?: string}} [options]
 */
export function attachDobField(el, options = {}) {
  if (!el || el.dataset.dobMaskBound === 'true') {
    return;
  }
  el.dataset.dobMaskBound = 'true';

  const errorClass = options.errorClass || DEFAULT_ERROR_CLASS;
  const invalidMessage = options.invalidMessage || DEFAULT_INVALID_MESSAGE;
  const futureMessage = options.futureMessage || DEFAULT_FUTURE_MESSAGE;
  const errorId = `${el.id || 'ghp-dob'}-error`;

  const reformat = (simulatedValue, digitsBefore) => {
    const formatted = formatDateMask(simulatedValue);
    if (el.value !== formatted) {
      el.value = formatted;
    }
    const caret = caretPositionAfterDigits(formatted, digitsBefore);
    el.setSelectionRange(caret, caret);
  };

  const validate = () =>
    validateDob(el, errorId, invalidMessage, futureMessage, errorClass);

  el.addEventListener('input', (e) => {
    if (isBulkInsert(e)) {
      reformat(expandAutofilledDate(el.value), DIGIT_LIMIT);
      validate();
      return;
    }
    const caret = el.selectionStart ?? el.value.length;
    reformat(el.value, countDigitsBefore(el.value, caret));
    validate();
  });

  el.addEventListener('keydown', (e) => {
    if (e.key !== 'Backspace') {
      return;
    }
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start !== end || start === 0 || el.value[start - 1] !== '/') {
      return;
    }
    e.preventDefault();
    const before = el.value.slice(0, start - 2);
    const after = el.value.slice(start);
    reformat(before + after, countDigitsBefore(before + after, before.length));
    validate();
  });

  el.addEventListener('blur', validate);
}

function validateDob(el, errorId, invalidMessage, futureMessage, errorClass) {
  const value = el.value;
  let message = '';
  if (value.length === FULL_LENGTH) {
    if (!isValidDob(value)) {
      message = invalidMessage;
    } else if (isFutureDate(value)) {
      message = futureMessage;
    }
  }
  setDobError(el, errorId, message, errorClass);
}

function setDobError(el, errorId, message, errorClass) {
  const row = el.closest('.form-row');
  if (message) {
    if (row) {
      row.classList.add('woocommerce-invalid');
      row.classList.remove('woocommerce-validated');
    }
    el.setAttribute('aria-invalid', 'true');
    el.setAttribute('aria-describedby', errorId);
    let span = document.getElementById(errorId);
    if (!span) {
      span = document.createElement('span');
      span.id = errorId;
      span.className = errorClass;
      span.setAttribute('role', 'alert');
      (row || el).insertAdjacentElement(row ? 'beforeend' : 'afterend', span);
    }
    span.textContent = message;
    return;
  }

  el.removeAttribute('aria-invalid');
  el.removeAttribute('aria-describedby');
  const span = document.getElementById(errorId);
  if (span && span.parentNode) {
    span.parentNode.removeChild(span);
  }
  if (!row) {
    return;
  }
  if (el.value.length === FULL_LENGTH) {
    row.classList.add('woocommerce-validated');
    row.classList.remove('woocommerce-invalid');
  } else {
    row.classList.remove('woocommerce-invalid', 'woocommerce-validated');
  }
}
